import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { DollarSign, Clock, TrendingUp, TrendingDown, Users, Home, Calendar, Filter, BarChart3 } from 'lucide-react';

export default function StaffCostsReportPage() {
  const [reportData, setReportData] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    groupBy: 'staff'
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [costRes, periodRes, trendRes] = await Promise.all([
        api.get('/reports/staff-costs', { params: filters }),
        api.get('/reports/cost-by-period'),
        api.get('/reports/cost-trends?months=6')
      ]);
      setReportData(costRes.data.data);
      setPeriodData(periodRes.data);
      setTrendData(trendRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async () => {
    setLoading(true);
    try {
      const params = {
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
        group_by: filters.groupBy
      };
      const response = await api.get('/reports/staff-costs', { params });
      setReportData(response.data.data);
    } catch (error) {
      toast.error('Failed to apply filters');
    } finally {
      setLoading(false);
    }
  };

  const getMarginColor = (margin) => {
    if (margin >= 20) return 'text-green-500';
    if (margin >= 10) return 'text-yellow-500';
    return 'text-red-500';
  };

  if (loading && !reportData) {
    return <div className="flex items-center justify-center p-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="staff-costs-report-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Staff Cost Reports</h1>
          <p className="text-muted-foreground">Analyze payroll costs, revenue, and margins</p>
        </div>
      </div>

      {/* Summary Cards */}
      {reportData?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Staff Cost</p>
                  <p className="text-2xl font-bold">${reportData.summary.staff_cost?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                </div>
                <DollarSign className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Hours</p>
                  <p className="text-2xl font-bold">{reportData.summary.hours?.toLocaleString()}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Revenue</p>
                  <p className="text-2xl font-bold">${reportData.summary.participant_revenue?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Margin</p>
                  <p className={`text-2xl font-bold ${getMarginColor(reportData.summary.margin_percentage)}`}>
                    ${reportData.summary.margin_estimate?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                {reportData.summary.margin_estimate >= 0 ? (
                  <TrendingUp className="h-8 w-8 text-green-500" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-500" />
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Margin %</p>
                  <p className={`text-2xl font-bold ${getMarginColor(reportData.summary.margin_percentage)}`}>
                    {reportData.summary.margin_percentage?.toFixed(1)}%
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Cost Breakdown</TabsTrigger>
          <TabsTrigger value="periods">By Period</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Date From</Label>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date To</Label>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Group By</Label>
                  <Select value={filters.groupBy} onValueChange={(v) => setFilters({ ...filters, groupBy: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="participant">Participant</SelectItem>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="hour_type">Hour Type</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button className="w-full" onClick={handleFilterChange} disabled={loading}>
                    Apply Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle>Cost Breakdown by {filters.groupBy}</CardTitle>
              <CardDescription>{reportData?.breakdown?.length || 0} item(s)</CardDescription>
            </CardHeader>
            <CardContent>
              {!reportData?.breakdown?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No data available for the selected filters</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {filters.groupBy === 'staff' && 'Staff Name'}
                        {filters.groupBy === 'participant' && 'Participant'}
                        {filters.groupBy === 'house' && 'House'}
                        {filters.groupBy === 'date' && 'Date'}
                        {filters.groupBy === 'hour_type' && 'Hour Type'}
                      </TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Total Cost</TableHead>
                      {filters.groupBy === 'staff' && <TableHead className="text-right">Avg Hourly</TableHead>}
                      <TableHead className="text-right">Line Items</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.breakdown.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">
                          {item.staff_name || item.participant_name || item.house_name || item.date || item.hour_type || '-'}
                        </TableCell>
                        <TableCell className="text-right">{item.total_hours}</TableCell>
                        <TableCell className="text-right">${item.total_cost?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                        {filters.groupBy === 'staff' && (
                          <TableCell className="text-right">${item.avg_hourly_cost?.toFixed(2)}</TableCell>
                        )}
                        <TableCell className="text-right">{item.line_items}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="periods">
          <Card>
            <CardHeader>
              <CardTitle>Cost by Payroll Period</CardTitle>
              <CardDescription>Total: ${periodData?.summary?.total_cost?.toLocaleString('en-AU', { minimumFractionDigits: 2 }) || 0}</CardDescription>
            </CardHeader>
            <CardContent>
              {!periodData?.data?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No period data available</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>Date Range</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Batches</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Total Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {periodData.data.map((period, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{period.period_no}</TableCell>
                        <TableCell>{period.start_date} - {period.end_date}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs ${period.period_status === 'OPEN' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                            {period.period_status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{period.batch_count}</TableCell>
                        <TableCell className="text-right">{period.total_hours}</TableCell>
                        <TableCell className="text-right font-medium">${period.total_cost?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <CardTitle>Cost Trends</CardTitle>
              <CardDescription>
                Monthly average: ${trendData?.summary?.avg_monthly_cost?.toLocaleString('en-AU', { minimumFractionDigits: 2 }) || 0}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!trendData?.data?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No trend data available</p>
                </div>
              ) : (
                <>
                  {/* Simple Bar Chart Visualization */}
                  <div className="mb-6">
                    <div className="flex items-end gap-2 h-40">
                      {trendData.data.map((month, idx) => {
                        const maxCost = Math.max(...trendData.data.map(m => m.total_cost));
                        const height = maxCost > 0 ? (month.total_cost / maxCost) * 100 : 0;
                        return (
                          <div key={idx} className="flex-1 flex flex-col items-center">
                            <div 
                              className="w-full bg-primary rounded-t transition-all hover:bg-primary/80"
                              style={{ height: `${height}%`, minHeight: month.total_cost > 0 ? '4px' : '0' }}
                              title={`$${month.total_cost.toLocaleString()}`}
                            />
                            <span className="text-xs mt-1 text-muted-foreground">{month.month?.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                        <TableHead className="text-right">Staff</TableHead>
                        <TableHead className="text-right">Change</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trendData.data.map((month, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{month.month}</TableCell>
                          <TableCell className="text-right">{month.total_hours}</TableCell>
                          <TableCell className="text-right">${month.total_cost?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell className="text-right">{month.staff_count}</TableCell>
                          <TableCell className="text-right">
                            {month.cost_change_pct !== undefined && (
                              <span className={month.cost_change_pct >= 0 ? 'text-red-500' : 'text-green-500'}>
                                {month.cost_change_pct >= 0 ? '+' : ''}{month.cost_change_pct}%
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
